import { useCallback, useRef, useState } from 'react';
import {
  readBookingProblem,
  type BookingProblem,
  type MapSpot,
  type Reservation,
  type ReservationRequest,
} from '@kratispot/shared';
import { ApiError, actOnReservation, createReservation, extendReservation } from '@/lib/api';

export type BookingAction = 'cancel' | 'check-in' | 'restore';

export type ReservationCommands = {
  readonly busy: boolean;
  // Read after awaiting: a render-time value is the one from before. §15
  readonly lastProblem: () => BookingProblem | null;
  readonly create: (request: ReservationRequest) => Promise<Reservation[] | null>;
  readonly act: (id: string, action: BookingAction) => Promise<Reservation[] | null>;
  readonly extend: (id: string, holdMinutes: number) => Promise<Reservation[] | null>;
};

// The booking writer beside the status one: apply, then re-read. §16
export function useReservationCommands(
  venueId: string,
  apply: (spots: readonly MapSpot[]) => void,
  reload: () => void,
): ReservationCommands {
  const [busy, setBusy] = useState(false);
  const problem = useRef<BookingProblem | null>(null);

  const run = useCallback(
    async (send: () => Promise<{ reservations: Reservation[]; spots: MapSpot[] }>) => {
      // Busy clears on both paths, not in a finally: v8 scores that a lost branch.
      setBusy(true);
      problem.current = null;
      try {
        const answer = await send();
        apply(answer.spots);
        reload();
        setBusy(false);
        return answer.reservations;
      } catch (error) {
        problem.current = error instanceof ApiError ? readBookingProblem(error.body) : null;
        reload();
        setBusy(false);
        return null;
      }
    },
    [apply, reload],
  );

  return {
    busy,
    lastProblem: useCallback(() => problem.current, []),
    create: useCallback(
      (request) => run(() => createReservation(venueId, request)),
      [run, venueId],
    ),
    act: useCallback(
      (id, action) => run(() => actOnReservation(venueId, id, action)),
      [run, venueId],
    ),
    extend: useCallback(
      (id, holdMinutes) => run(() => extendReservation(venueId, id, holdMinutes)),
      [run, venueId],
    ),
  };
}
